/** Read-only operational view of deterministic chat outcome events. */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../AuthContext';
import { Card } from '../components/SharedComponents';
import { API_BASE } from '../config';
import colors from '../utils/adminColors';

const DAY_OPTIONS = [7, 30, 90, 365];
const KIND_OPTIONS = [
  { value: '', label: 'All outcome families' },
  { value: 'grounding_guard', label: 'Grounding guard' },
  { value: 'price_fact', label: 'Pricing facts' },
  { value: 'leadtime_fact', label: 'Lead-time facts' },
  { value: 'delivery_fact', label: 'Delivery facts' },
];
const CATEGORY_COLORS = {
  allowed: colors.success,
  answered: colors.success,
  guard_modified: colors.warning,
  guard_discarded: colors.danger,
  routing_or_scope_restriction: colors.warning,
  unsupported_or_ambiguous_window: colors.warning,
  sufficiency_floor: colors.textSecondary,
  absent_data: colors.textSecondary,
  engine_or_config_problem: colors.danger,
  producer_disagreement: colors.danger,
  fell_through: colors.warning,
  unclassified: colors.textSecondary,
};
const selectStyle = {
  minWidth: '180px', height: '40px', padding: '0 12px',
  border: `1px solid ${colors.border}`, borderRadius: '8px',
  backgroundColor: colors.surface, color: colors.darkText, fontSize: '14px',
};

function FilterField({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <span style={{ color: colors.textSecondary, fontSize: '12px', fontWeight: 600 }}>{label}</span>
      {children}
    </label>
  );
}

function OutcomeRow({ outcome }) {
  const categoryColor = CATEGORY_COLORS[outcome.category] || colors.textSecondary;
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'minmax(180px, 1.2fr) minmax(220px, 1.6fr) 88px',
      gap: '16px', alignItems: 'center', padding: '14px 0',
      borderTop: `1px solid ${colors.borderLight}`,
    }}>
      <div>
        <div style={{ color: colors.darkText, fontSize: '14px', fontWeight: 600 }}>{outcome.label}</div>
        <div style={{ color: colors.textMuted, fontFamily: 'monospace', fontSize: '11px', marginTop: '3px' }}>
          {outcome.outcome}
        </div>
      </div>
      <div>
        <span style={{
          display: 'inline-block', padding: '3px 9px', borderRadius: '999px',
          color: categoryColor, backgroundColor: `${categoryColor}12`,
          border: `1px solid ${categoryColor}35`, fontSize: '12px', fontWeight: 600,
        }}>
          {outcome.category_label}
        </span>
      </div>
      <div style={{ color: colors.darkText, fontSize: '22px', fontWeight: 700, textAlign: 'right' }}>
        {outcome.count.toLocaleString()}
      </div>
    </div>
  );
}

function OutcomeFamily({ group }) {
  return (
    <Card padding="20px 24px" style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '20px' }}>
        <div>
          <h2 style={{ color: colors.darkText, fontSize: '18px', margin: 0 }}>{group.kind_label}</h2>
          <div style={{ color: colors.textMuted, fontFamily: 'monospace', fontSize: '11px', marginTop: '4px' }}>
            {group.kind}
          </div>
        </div>
        <div style={{ color: colors.textSecondary, fontSize: '13px', whiteSpace: 'nowrap' }}>
          <strong style={{ color: colors.darkText, fontSize: '18px' }}>{group.event_count.toLocaleString()}</strong>{' '}events
        </div>
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'minmax(180px, 1.2fr) minmax(220px, 1.6fr) 88px',
        gap: '16px', padding: '18px 0 8px', color: colors.textMuted, fontSize: '11px',
        fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
      }}>
        <span>Recorded outcome</span><span>What it means</span><span style={{ textAlign: 'right' }}>Count</span>
      </div>
      {group.outcomes.map(outcome => <OutcomeRow key={outcome.outcome} outcome={outcome} />)}
    </Card>
  );
}

function AdminChatOutcomes() {
  const { token } = useAuth();
  const [days, setDays] = useState(30);
  const [companyId, setCompanyId] = useState('');
  const [kind, setKind] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestSequence = useRef(0);

  const fetchReport = useCallback(async () => {
    const requestId = requestSequence.current + 1;
    requestSequence.current = requestId;
    setLoading(true);
    setReport(null);
    setError(null);
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (companyId) params.set('company_id', companyId);
      if (kind) params.set('kind', kind);
      const response = await fetch(`${API_BASE}/api/admin/chat-outcomes?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${response.status}`);
      }
      const nextReport = await response.json();
      if (requestSequence.current === requestId) setReport(nextReport);
    } catch (fetchError) {
      if (requestSequence.current === requestId) {
        setReport(null);
        setError(fetchError.message || 'Failed to load chat outcomes');
      }
    } finally {
      if (requestSequence.current === requestId) setLoading(false);
    }
  }, [token, days, companyId, kind]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const companies = report?.companies || [];
  const kinds = report?.kinds || [];
  return (
    <div style={{ padding: '32px', backgroundColor: colors.lightGray, minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ color: colors.darkText, fontSize: 'var(--font-size-hero)', marginBottom: '8px' }}>Chat Outcomes</h1>
        <p style={{ color: colors.textMuted, fontSize: 'var(--font-size-base)', margin: 0 }}>
          See where deterministic chat paths answered, needed clarification, lacked data, or failed.
        </p>
      </div>

      <Card padding="16px 20px" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end' }}>
          <FilterField label="Time window">
            <select value={days} onChange={event => setDays(Number(event.target.value))} style={selectStyle}>
              {DAY_OPTIONS.map(value => <option key={value} value={value}>Last {value} calendar days</option>)}
            </select>
          </FilterField>
          <FilterField label="Company">
            <select value={companyId} onChange={event => setCompanyId(event.target.value)} style={selectStyle}>
              <option value="">All companies</option>
              {companies.map(company => (
                <option key={company.company_id} value={company.company_id}>
                  {company.company_name || `Company ${company.company_id} (record missing)`}
                </option>
              ))}
            </select>
          </FilterField>
          <FilterField label="Outcome family">
            <select value={kind} onChange={event => setKind(event.target.value)} style={selectStyle}>
              {KIND_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </FilterField>
          <button type="button" onClick={fetchReport} disabled={loading} style={{
            height: '40px', padding: '0 16px', border: 0, borderRadius: '8px',
            backgroundColor: colors.accent, color: colors.white,
            cursor: loading ? 'default' : 'pointer', fontSize: '14px', fontWeight: 600,
            opacity: loading ? 0.65 : 1,
          }}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </Card>

      <div style={{
        padding: '14px 16px', marginBottom: '16px', border: `1px solid ${colors.info}35`,
        borderRadius: '8px', backgroundColor: `${colors.info}0D`, color: colors.textSecondary,
        fontSize: '13px', lineHeight: 1.5,
      }}>
        These are outcome events, not unique customer requests. Each family is shown separately because one
        request can create more than one event; combining them would produce a misleading success rate.
      </div>

      {report?.filters && (
        <div style={{ color: colors.textMuted, fontSize: '12px', margin: '0 0 12px 2px' }}>
          Inclusive window: {report.filters.start_date} through {report.filters.end_date}
        </div>
      )}
      {error && (
        <Card padding="20px" style={{ borderLeft: `4px solid ${colors.danger}` }}>
          <div style={{ color: colors.danger, fontWeight: 700 }}>Report unavailable</div>
          <div style={{ color: colors.textSecondary, fontSize: '14px', marginTop: '6px' }}>{error}</div>
        </Card>
      )}
      {!error && loading && !report && (
        <Card padding="32px"><div style={{ color: colors.textMuted }}>Loading outcome events…</div></Card>
      )}
      {!error && !loading && kinds.length === 0 && (
        <Card padding="32px">
          <div style={{ color: colors.darkText, fontWeight: 700 }}>No outcome events found</div>
          <div style={{ color: colors.textMuted, fontSize: '14px', marginTop: '6px' }}>
            Try a longer time window or clear the company and outcome-family filters.
          </div>
        </Card>
      )}
      {!error && kinds.map(group => <OutcomeFamily key={group.kind} group={group} />)}
    </div>
  );
}

export default AdminChatOutcomes;
